/**
 * Session Status Component
 * Displays session status and provides session management controls
 */
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Alert,
    LinearProgress,
    Collapse,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    Security as SecurityIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    WifiOff as OfflineIcon,
    Wifi as OnlineIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Logout as LogoutIcon,
    Timer as TimerIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import useSessionManager from '../hooks/useSessionManager';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 12,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    background: theme.palette.background.paper,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 500,
}));

const SessionStatus = ({ 
    compact = false, 
    showDetails = true, 
    onRefresh, 
    onLogout 
}) => {
    const [expanded, setExpanded] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    
    const { user } = useSelector(state => state.auth);
    const {
        sessionInfo,
        isOnline,
        timeUntilExpiry,
        getSessionWarnings,
        getSessionStats,
        refreshSession,
        handleLogout: sessionLogout
    } = useSessionManager();

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await refreshSession();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Session refresh failed:', error);
        } finally {
            setRefreshing(false);
        }
    };

    // Handle logout
    const handleLogout = async () => {
        try {
            await sessionLogout();
            if (onLogout) onLogout();
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    // Get status icon
    const getStatusIcon = () => {
        if (!sessionInfo?.isAuthenticated) {
            return <ErrorIcon color="error" />;
        }
        
        if (sessionInfo?.isRefreshing) {
            return <RefreshIcon color="primary" />;
        }
        
        if (sessionInfo?.needsRefresh) {
            return <WarningIcon color="warning" />;
        }
        
        return <CheckIcon color="success" />;
    };

    // Get status text
    const getStatusText = () => {
        if (!sessionInfo?.isAuthenticated) {
            return 'Not Authenticated';
        }
        
        if (sessionInfo?.isRefreshing) {
            return 'Refreshing Session';
        }
        
        if (sessionInfo?.needsRefresh) {
            return 'Session Expiring';
        }
        
        return 'Session Active';
    };

    // Get status color
    const getStatusColor = () => {
        if (!sessionInfo?.isAuthenticated) {
            return 'error';
        }
        
        if (sessionInfo?.isRefreshing) {
            return 'primary';
        }
        
        if (sessionInfo?.needsRefresh) {
            return 'warning';
        }
        
        return 'success';
    };

    // Format time until expiry
    const formatTimeUntilExpiry = () => {
        if (!timeUntilExpiry) return 'Unknown';
        
        const minutes = Math.floor(timeUntilExpiry / (1000 * 60));
        const seconds = Math.floor((timeUntilExpiry % (1000 * 60)) / 1000);
        
        if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        }
        
        return `${seconds}s`;
    };

    // Get progress value for session expiry
    const getProgressValue = () => {
        if (!sessionInfo?.accessTokenExpiry) return 0;

        const now = new Date();
        const expiry = new Date(sessionInfo.accessTokenExpiry);
        const total = expiry.getTime() - (now.getTime() - 30 * 60 * 1000); // 30 minutes total
        const remaining = timeUntilExpiry || 0;

        return Math.max(0, Math.min(100, (remaining / total) * 100));
    };

    if (compact) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                {getStatusIcon()}
                <Chip 
                    label={getStatusText()}
                    color={getStatusColor()}
                    size="small"
                    variant="outlined"
                />
                {!isOnline && (
                    <Chip 
                        icon={<OfflineIcon />}
                        label="Offline"
                        color="error"
                        size="small"
                    />
                )}
            </Box>
        );
    }

    return (
        <StyledCard>
            <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {getStatusIcon()}
                    <Box flex={1}>
                        <Typography variant="h6" gutterBottom>
                            {getStatusText()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {user?.name || 'User'} • {user?.role || 'Unknown Role'}
                        </Typography>
                    </Box>
                    {showDetails && (
                        <IconButton 
                            onClick={() => setExpanded(!expanded)}
                            size="small"
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    )}
                </Box>

                {/* Status indicators */}
                <Box display="flex" gap={1} mb={2}>
                    <Chip 
                        icon={isOnline ? <OnlineIcon /> : <OfflineIcon />}
                        label={isOnline ? 'Online' : 'Offline'}
                        color={isOnline ? 'success' : 'error'}
                        size="small"
                    />
                    {sessionInfo?.isRefreshing && (
                        <Chip 
                            icon={<RefreshIcon />}
                            label="Refreshing"
                            color="primary"
                            size="small"
                        />
                    )}
                    {sessionInfo?.needsRefresh && (
                        <Chip 
                            icon={<WarningIcon />}
                            label="Expiring"
                            color="warning"
                            size="small"
                        />
                    )}
                </Box>

                {/* Session expiry progress */}
                {timeUntilExpiry && (
                    <Box mb={2}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">
                                Session Expires
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formatTimeUntilExpiry()}
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate"
                            value={getProgressValue()}
                            color={getProgressValue() < 20 ? 'error' : getProgressValue() < 50 ? 'warning' : 'primary'}
                            sx={{ height: 4, borderRadius: 2 }}
                        />
                    </Box>
                )}

                {/* Warnings */}
                {getSessionWarnings().length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            {getSessionWarnings().join(', ')}
                        </Typography>
                    </Alert>
                )}

                {/* Expandable details */}
                <Collapse in={expanded}>
                    <Box>
                        <Divider sx={{ my: 2 }} />
                        
                        <List dense>
                            <ListItem>
                                <ListItemIcon>
                                    <PersonIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="User"
                                    secondary={user?.name || 'Unknown'}
                                />
                            </ListItem>

                            <ListItem>
                                <ListItemIcon>
                                    <SecurityIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Role"
                                    secondary={user?.role || 'Unknown'}
                                />
                            </ListItem>

                            <ListItem>
                                <ListItemIcon>
                                    <TimerIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Last Activity"
                                    secondary={getSessionStats().lastActivity.toLocaleString()}
                                />
                            </ListItem>

                            {sessionInfo?.accessTokenExpiry && (
                                <ListItem>
                                    <ListItemIcon>
                                        <TimerIcon />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Session Expires"
                                        secondary={new Date(sessionInfo.accessTokenExpiry).toLocaleString()}
                                    />
                                </ListItem>
                            )}
                        </List>

                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" gap={2}>
                            <StyledButton 
                                variant="outlined"
                                onClick={handleRefresh}
                                disabled={refreshing}
                                startIcon={refreshing ? <LinearProgress size={16} /> : <RefreshIcon />}
                                fullWidth
                            >
                                {refreshing ? 'Refreshing...' : 'Refresh Session'}
                            </StyledButton>

                            <StyledButton 
                                variant="outlined"
                                color="error"
                                onClick={handleLogout}
                                startIcon={<LogoutIcon />}
                                fullWidth
                            >
                                Logout
                            </StyledButton>
                        </Box>
                    </Box>
                </Collapse>
            </CardContent>
        </StyledCard>
    );
};

export default SessionStatus;